import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button, Divider, Empty, Mentions, Modal, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { CloseOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, FilePdfOutlined, SendOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../../../variables/constants";
import { openSse } from "../../../../../variables/api/sseClient";
import DiscussionSinistreDoc from "../../../docs/DiscussionSinistreDoc";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const { Text } = Typography;

// Fenêtre d'édition d'un message par son auteur (identique à la garde backend).
const EDIT_WINDOW_MIN = 20;

const initials = (user) => {
    if (!user) return "?";
    const n = `${user.personne?.prenom || user.prenom || ""} ${user.personne?.nom || user.nom || ""}`.trim();
    return n
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
};

const displayName = (user) => {
    if (!user) return "Inconnu";
    return `${user.personne?.prenom || user.prenom || ""} ${user.personne?.nom || user.nom || ""}`.trim() || user.username || "Inconnu";
};

const isOwnMessage = (msg) =>
    msg.auteur?.codeId === LOGGED_USER?.codeId;

const isSameDay = (a, b) =>
    dayjs(a).format("YYYY-MM-DD") === dayjs(b).format("YYYY-MM-DD");

const DateSeparator = ({ date }) => (
    <div style={{ textAlign: "center", margin: "16px 0" }}>
        <Tag style={{ borderRadius: 20, fontSize: 12, padding: "2px 14px" }}>
            {dayjs(date).format("dddd D MMMM YYYY")}
        </Tag>
    </div>
);

const Bubble = ({ msg, onDelete, onEdit, readOnly }) => {
    const own = isOwnMessage(msg);
    const name = displayName(msg.auteur);
    const avatar = initials(msg.auteur);
    // Édition possible par l'auteur pendant EDIT_WINDOW_MIN minutes après la création.
    const canEdit = own && !readOnly && dayjs().diff(dayjs(msg.createdAt), "minute") < EDIT_WINDOW_MIN;
    const modifie = msg.updatedAt && !dayjs(msg.updatedAt).isSame(dayjs(msg.createdAt));

    const renderContenu = (text, mentions) => {
        if (!mentions || mentions.length === 0) return text;
        let result = text;
        mentions.forEach((u) => {
            const n = displayName(u);
            result = result.replaceAll(
                `@${n}`,
                `__MENTION_${u.codeId}__${n}__END__`
            );
        });
        return result.split(/(__MENTION_.*?__END__)/).map((part, i) => {
            const m = part.match(/^__MENTION_(.*?)__(.*?)__END__$/);
            if (m) {
                return (
                    <Text key={i} strong style={{ color: own ? "#fff" : "#667eea" }}>
                        @{m[2]}
                    </Text>
                );
            }
            return <span key={i}>{part}</span>;
        });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: own ? "row-reverse" : "row",
                alignItems: "flex-end",
                gap: 8,
                marginBottom: 12,
            }}
        >
            <Tooltip title={name}>
                <Avatar
                    size={34}
                    style={{
                        background: own
                            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            : "#e8e8e8",
                        color: own ? "#fff" : "#555",
                        fontSize: 13,
                        flexShrink: 0,
                    }}
                >
                    {avatar}
                </Avatar>
            </Tooltip>

            <div style={{ maxWidth: "70%" }}>
                {!own && (
                    <Text
                        type="secondary"
                        style={{ fontSize: 11, display: "block", marginBottom: 2, paddingLeft: 4 }}
                    >
                        {name}
                    </Text>
                )}
                <div
                    style={{
                        background: own
                            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            : "#f0f0f0",
                        color: own ? "#fff" : "#222",
                        borderRadius: own ? "18px 4px 18px 18px" : "4px 18px 18px 18px",
                        padding: "10px 14px",
                        fontSize: 14,
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                        position: "relative",
                    }}
                >
                    {renderContenu(msg.contenu, msg.mentions)}
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: own ? "flex-end" : "flex-start",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 3,
                        paddingLeft: own ? 0 : 4,
                        paddingRight: own ? 4 : 0,
                    }}
                >
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {dayjs(msg.createdAt).format("HH:mm")}
                    </Text>
                    {modifie && (
                        <Text type="secondary" style={{ fontSize: 10, fontStyle: "italic" }}>
                            modifié
                        </Text>
                    )}
                    {canEdit && (
                        <Tooltip title={`Modifier (${EDIT_WINDOW_MIN} min)`}>
                            <EditOutlined
                                onClick={() => onEdit(msg)}
                                style={{ fontSize: 11, color: "#aaa", cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                    {own && !readOnly && (
                        <Tooltip title="Supprimer">
                            <DeleteOutlined
                                onClick={() => onDelete(msg.codeId)}
                                style={{ fontSize: 11, color: "#aaa", cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

const DiscussionsTabV2 = ({ sinistre }) => {
    const readOnly = !!sinistre?.lectureSeule;
    const [messages, setMessages] = useState([]);
    const [users, setUsers] = useState([]);
    const [contenu, setContenu] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedMentions, setSelectedMentions] = useState([]);
    // codeId du message en cours d'ÉDITION (null = nouveau message).
    const [editingId, setEditingId] = useState(null);
    const [exporting, setExporting] = useState(false);
    const bottomRef = useRef(null);
    const docRef = useRef(null);

    const isPoolUser = LOGGED_USER?.profil?.typeOrganisme === "POOL";

    const fetchMessages = async () => {
        if (!sinistre?.codeId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/discussion/sinistre/${sinistre.codeId}`);
            setMessages(res.data);
        } catch {
            toast.error("Impossible de charger les discussions");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            let res;
            if (isPoolUser) {
                res = await axiosInstance.get("/api/utilisateur/all");
            } else {
                const codeOrganisme = LOGGED_USER?.profil?.organisme?.code;
                res = await axiosInstance.get(`/api/utilisateur/organisme/code/${codeOrganisme}`);
            }
            // Tri alphabétique global de la liste des mentions
            const sorted = (res.data || []).slice().sort((a, b) =>
                displayName(a).localeCompare(displayName(b), "fr", { sensitivity: "base" })
            );
            setUsers(sorted);
        } catch {
            // non-blocking — mentions still work without the list
        }
    };

    useEffect(() => {
        fetchMessages();
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sinistre?.codeId]);

    // SSE : le fil se met à jour EN TEMPS RÉEL quand un autre utilisateur écrit, sans rafraîchir.
    // Token de flux à courte durée (openSse) — pas le JWT de session dans l'URL. Event "discussion" → recharge.
    useEffect(() => {
        if (!sinistre?.codeId) return undefined;
        const cleanup = openSse(`/api/discussion/stream/${sinistre.codeId}`, "discussion", () => fetchMessages());
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sinistre?.codeId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const mentionOptions = users
        .filter((u) => u.codeId !== LOGGED_USER?.codeId)
        .map((u) => ({
            value: displayName(u),
            label: (
                <Space>
                    <Avatar size={22} style={{ background: "#667eea", fontSize: 11 }}>
                        {initials(u)}
                    </Avatar>
                    <span>{displayName(u)}</span>
                </Space>
            ),
        }));

    const handleSend = async () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        const text = contenu.trim();
        if (!text) return;
        setSending(true);
        try {
            const mentionCodeIds = selectedMentions
                .map((name) => users.find((u) => displayName(u) === name)?.codeId)
                .filter(Boolean);

            if (editingId) {
                // Édition d'un message existant (< 20 min, auteur only côté backend).
                await axiosInstance.put(`/api/discussion/${editingId}`, { contenu: text, mentionCodeIds });
            } else {
                await axiosInstance.post(`/api/discussion/sinistre/${sinistre.codeId}`, {
                    contenu: text,
                    mentionCodeIds,
                });
            }
            setContenu("");
            setSelectedMentions([]);
            setEditingId(null);
            await fetchMessages();
        } catch (err) {
            const msg = err?.response?.data?.message
                || (editingId ? "Impossible de modifier ce message (délai dépassé ?)" : "Erreur lors de l'envoi du message");
            toast.error(msg);
        } finally {
            setSending(false);
        }
    };

    // Charge le message à éditer dans le composeur.
    const handleEditStart = (msg) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setEditingId(msg.codeId);
        setContenu(msg.contenu || "");
        setSelectedMentions((msg.mentions || []).map(displayName));
    };

    const handleEditCancel = () => {
        setEditingId(null);
        setContenu("");
        setSelectedMentions([]);
    };

    const handleDelete = (codeId) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        // Confirmation avant suppression définitive.
        Modal.confirm({
            title: "Supprimer ce message ?",
            icon: <ExclamationCircleOutlined />,
            content: "Cette action est définitive.",
            okText: "Supprimer",
            okButtonProps: { danger: true },
            cancelText: "Annuler",
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/api/discussion/${codeId}`);
                    setMessages((prev) => prev.filter((m) => m.codeId !== codeId));
                    if (editingId === codeId) handleEditCancel();
                } catch {
                    toast.error("Erreur lors de la suppression");
                }
            },
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Export PDF du fil sur l'EN-TÊTE PoolIns, en présentation « conversation » (html2pdf).
    const exportPdf = async () => {
        if (!messages.length) {
            toast.warn("Aucun message à exporter");
            return;
        }
        const el = docRef.current;
        if (!el) return;
        setExporting(true);
        try {
            const html2pdf = (await import("html2pdf.js")).default;
            const numero = sinistre?.numSinistre || sinistre?.numeroSinistreM || "sinistre";
            const opt = {
                margin: [0, 0, 0, 0],
                filename: `Discussion_${String(numero).replace(/[^\w-]/g, "_")}_${dayjs().format("YYYYMMDD")}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                pagebreak: { mode: ["css", "legacy"] },
            };
            await html2pdf().set(opt).from(el).save();
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de l'export PDF");
        } finally {
            setExporting(false);
        }
    };

    const handleMentionSelect = (option) => {
        setSelectedMentions((prev) =>
            prev.includes(option.value) ? prev : [...prev, option.value]
        );
    };

    return (
        <>
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "60vh",
                minHeight: 420,
                border: "1px solid #f0f0f0",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fafafa",
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: "12px 20px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0,
                }}
            >
                <div>
                    <Text strong style={{ color: "#fff", fontSize: 15 }}>
                        Discussions
                    </Text>
                    <br />
                    <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
                        Sinistre N° {sinistre?.numSinistre}
                    </Text>
                </div>
                <Space size={8}>
                    <Tag
                        style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            color: "#fff",
                            borderRadius: 20,
                            fontSize: 12,
                        }}
                    >
                        {messages.length} message{messages.length !== 1 ? "s" : ""}
                    </Tag>
                    <Tooltip title="Exporter le fil en PDF">
                        <Button
                            size="small"
                            icon={<FilePdfOutlined />}
                            onClick={exportPdf}
                            loading={exporting}
                            disabled={!messages.length}
                            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff", borderRadius: 8 }}
                        >
                            PDF
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            {/* Messages */}
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "16px 20px",
                    background: "#f7f8fc",
                }}
            >
                {loading ? (
                    <div style={{ textAlign: "center", paddingTop: 40 }}>
                        <Spin />
                    </div>
                ) : messages.length === 0 ? (
                    <Empty
                        description="Aucune discussion pour ce dossier"
                        style={{ marginTop: 40 }}
                    />
                ) : (
                    messages.map((msg, idx) => {
                        const showDate =
                            idx === 0 ||
                            !isSameDay(messages[idx - 1].createdAt, msg.createdAt);
                        return (
                            <React.Fragment key={msg.codeId}>
                                {showDate && <DateSeparator date={msg.createdAt} />}
                                <Bubble msg={msg} readOnly={readOnly} onDelete={handleDelete} onEdit={handleEditStart} />
                            </React.Fragment>
                        );
                    })
                )}
                <div ref={bottomRef} />
            </div>

            <Divider style={{ margin: 0 }} />

            {/* Input */}
            <div
                style={{
                    padding: "12px 16px",
                    background: "#fff",
                    flexShrink: 0,
                }}
            >
                {editingId ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <Text style={{ fontSize: 11, color: "#764ba2", fontWeight: 600 }}>
                            <EditOutlined /> Modification du message
                        </Text>
                        <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleEditCancel}
                            style={{ fontSize: 11, color: "#888" }}>
                            Annuler
                        </Button>
                    </div>
                ) : (
                    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>
                        Tapez @ pour mentionner un utilisateur · Entrée pour envoyer
                    </div>
                )}
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                    <Avatar
                        size={32}
                        style={{
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            fontSize: 12,
                            flexShrink: 0,
                            marginBottom: 2,
                        }}
                    >
                        {initials(LOGGED_USER)}
                    </Avatar>
                    <div style={{ flex: 1 }}>
                        <Mentions
                            value={contenu}
                            onChange={setContenu}
                            onKeyDown={handleKeyDown}
                            onSelect={handleMentionSelect}
                            prefix="@"
                            options={mentionOptions}
                            disabled={readOnly}
                            placeholder={readOnly ? "Dossier en lecture seule" : "Écrire un message..."}
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            style={{ borderRadius: 20, fontSize: 14 }}
                            filterOption={(input, option) =>
                                option.value.toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </div>
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSend}
                        loading={sending}
                        disabled={!contenu.trim() || readOnly}
                        style={{
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            border: "none",
                            borderRadius: 20,
                            height: 36,
                            marginBottom: 2,
                        }}
                    >
                        {editingId ? "Modifier" : "Envoyer"}
                    </Button>
                </div>
            </div>
        </div>

        {/* Document imprimable (en-tête PoolIns) rendu HORS ÉCRAN pour l'export PDF. */}
        <div style={{ position: "fixed", left: -10000, top: 0, width: "210mm" }} aria-hidden="true">
            <DiscussionSinistreDoc ref={docRef} sinistre={sinistre} messages={messages} />
        </div>
        </>
    );
};

export default DiscussionsTabV2;
